import * as zod from 'zod';
import SectionHeader from "../CoreComponents/Details/SectionHeader";
import { splitOnCapitals } from '../../Helpers/Functions';
import FormInput from '../CoreComponents/ReactFormComponents/FormInput';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useCallback } from 'react';
import { useScrollLock, useEscapeKey } from '../../hooks/useScrollLock';

type TProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: TaskLinkFormType) => Promise<void>;
    initialData?: TaskLinkFormType | null;
    mode?: 'add' | 'edit';
}

const schema = zod.object({
    linkUrl: zod.string().min(1, { message: "Please enter the link URL" }).url({ message: "Please enter a valid URL" }),
    linkText: zod.string().min(1, { message: "Please enter the link text" }),
    linkCategory: zod.string().min(1, { message: "Please enter the link category" }),
});

export type TaskLinkFormType = zod.infer<typeof schema>;

export default function AddTaskLinkModal({ isOpen, onClose, onSubmit, initialData, mode = 'add' }: TProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<TaskLinkFormType>({
        resolver: zodResolver(schema),
        defaultValues: {
            linkUrl: "",
            linkText: "",
            linkCategory: ""
        }
    });

    // Reset form with initial data when modal opens or initialData changes
    useEffect(() => {
        if (isOpen && initialData) {
            reset(initialData);
        } else if (isOpen) {
            reset({ linkUrl: "", linkText: "", linkCategory: "" });
        }
    }, [isOpen, initialData, reset]);

    const handleCloseModal = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    useEscapeKey(isOpen, handleCloseModal);
    useScrollLock(isOpen);

    const handleFormSubmit = async (data: TaskLinkFormType) => {
        try {
            await onSubmit(data);
        } catch (error) {
            console.error("Error adding task link:", error);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-link-modal-title"
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
            <div 
                className="bg-white dark:bg-gray-800 dark:text-white rounded-lg shadow-xl w-full max-w-screen-md p-6"
            >
                <div className="mt-4 mb-8">
                    <SectionHeader 
                        title={mode === 'edit' ? "Edit Task Link" : "Add New Task Link"} 
                        id="task-link-modal-title"
                    />
                </div>

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <FormInput
                        dataTestId="link-url-input"
                        required={true}
                        errors={errors}
                        defaultValue=""
                        inputKey="linkUrl"
                        registeredProps={register('linkUrl')}
                        label={splitOnCapitals("LinkUrl")}
                        placeholder="https://example.com"
                    />
                    <FormInput
                        dataTestId="link-text-input"
                        required={true}
                        errors={errors}
                        defaultValue=""
                        inputKey="linkText"
                        registeredProps={register('linkText')}
                        label={splitOnCapitals("LinkText")}
                        placeholder="Link Text"
                    />
                    <FormInput
                        dataTestId="link-category-input"
                        required={true}
                        errors={errors}
                        defaultValue=""
                        inputKey="linkCategory"
                        registeredProps={register('linkCategory')}
                        label={splitOnCapitals("LinkCategory")}
                        placeholder="Category (e.g., Documentation, Reference)"
                    />

                    <div className='flex justify-between mt-16'>
                        <button
                            type="button"
                            data-testid="add-task-link-form-cancel-button"
                            onClick={handleCloseModal}
                            className='mt-3 text-gray-900 bg-gray-500 dark:text-gray-900 dark:bg-gray-500'
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="add-task-link-form-submit-button"
                            className='text-white bg-orange-1000 dark:text-gray-800 dark:bg-orange-1000'
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
